import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/apiClient';
import Layout from '../components/layout/Layout';

const Settings = () => {
  const { currentUser } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Check if password meets minimum requirements
  const isPasswordValid = passwordData.newPassword.length >= 8;
  const passwordsMatch = passwordData.newPassword === passwordData.confirmPassword;

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate new password
    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    // Validate password confirmation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.patch('/auth/update-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setSuccess('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password. Please check your current password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4 max-w-3xl">
        <div className="bg-white rounded-lg shadow-md overflow-hidden p-4">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage your account security and preferences
            </p>
          </div>

          <div className="p-6">
            {/* Change Password Section */}
            <div className="mb-8">
              <h2 className="text-xl font-medium text-gray-900 mb-4">Change Password</h2>
              
              {error && (
                <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-4 p-4 rounded-md bg-green-50 border border-green-200">
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                      Current Password
                    </label>
                    <div className="mt-1">
                      <input
                        id="currentPassword"
                        name="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className="form-input block w-full rounded-md"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <div className="mt-1">
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        onFocus={() => setPasswordFocused(true)}
                        onBlur={() => setPasswordFocused(false)}
                        className="form-input block w-full rounded-md"
                      />
                    </div>
                    {(passwordFocused || passwordData.newPassword) && (
                      <div className="mt-2 text-sm">
                        <p style={{ 
                          color: isPasswordValid ? 'var(--color-green-600)' : 'var(--color-gray-500)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '16px', 
                            height: '16px', 
                            borderRadius: '50%', 
                            backgroundColor: isPasswordValid ? 'var(--color-green-500)' : 'var(--color-gray-300)',
                            color: 'white',
                            textAlign: 'center',
                            lineHeight: '16px',
                            fontSize: '12px'
                          }}>
                            {isPasswordValid ? '✓' : ''}
                          </span>
                          Must be at least 8 characters
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <div className="mt-1">
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className="form-input block w-full rounded-md"
                      />
                    </div>
                    {passwordData.confirmPassword && (
                      <div className="mt-2 text-sm">
                        <p style={{ 
                          color: passwordsMatch ? 'var(--color-green-600)' : 'var(--color-red-600)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '16px', 
                            height: '16px', 
                            borderRadius: '50%', 
                            backgroundColor: passwordsMatch ? 'var(--color-green-500)' : 'var(--color-red-500)',
                            color: 'white',
                            textAlign: 'center',
                            lineHeight: '16px',
                            fontSize: '12px'
                          }}>
                            {passwordsMatch ? '✓' : '✗'}
                          </span>
                          {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-smd font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-4 btn btn-primary"
                    >
                      {isLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Email Notifications Section */}
            {/*<div className="border-t border-gray-200 pt-6">
                          <h2 className="text-xl font-medium text-gray-900 mb-4">Email Notifications</h2>
                          <div className="space-y-4">
                            <div className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  id="marketing"
                                  name="marketing"
                                  type="checkbox"
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                              </div>
                              <div className="ml-3 text-sm">
                                <label htmlFor="marketing" className="font-medium text-gray-700">Marketing emails</label>
                                <p className="text-gray-500">Receive emails about new features, promotions, and product updates</p>
                              </div>
                            </div>
            
                            <div className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  id="security"
                                  name="security"
                                  type="checkbox"
                                  defaultChecked={true}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                              </div>
                              <div className="ml-3 text-sm">
                                <label htmlFor="security" className="font-medium text-gray-700">Security alerts</label>
                                <p className="text-gray-500">Receive emails about security updates and account activity</p>
                              </div>
                            </div>
                          </div>
                        </div>*/}

            {/* Account Deactivation Section */}
            {/*
                        <div className="border-t border-gray-200 pt-6 mt-6">
                          <h2 className="text-xl font-medium text-gray-900 mb-4">Account Deactivation</h2>
                          <p className="text-sm text-gray-600 mb-4">
                            Deactivating your account will disable your profile and remove your data from our active systems.
                            This action can be reversed by contacting support.
                          </p>
                          <button
                            type="button"
                            className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            onClick={() => alert('Account deactivation feature will be implemented in the future.')}
                          >
                            Deactivate Account
                          </button>
                        </div>*/}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings; 